import React, { useContext, useState } from "react";
import { get, isEmpty, isNull } from "lodash";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ManagePlanLayout,
  AddPlans,
  CsplTable as Table,
  Link,
  AddToolTip,
  LoaderWrapper,
} from "../../../components";
import {
  MANAGE_PLAN_ROUTES,
  FLOW_TYPES,
  getPathWithParam,
  OPTIONS_DATA_MAPPER,
  managePlanFormNames,
  formFields,
} from "../../../utils";
import {
  clearLocalCacheByModel,
  createPlanStore,
  setManagePlanLocalCache,
} from "../../../contexts";
import { useDeepEffect, useRouterParams, useRequest } from "../../../abstracts";
import PlanFilterComponent from "./PlanFilterComponent";
import {
  getInvestmentType,
  listInvestment,
  listAdvisor,
  listCustodian,
} from "../../../services";
import { toLower } from "lodash";

const columns = [
  {
    label: "Investment Name",
    className: "column-investment-name",
    columnName: "name",
    link: MANAGE_PLAN_ROUTES.ADD_INVESTMENTS,
  },
  {
    label: "Investment Type",
    className: "column-investment-type",
    columnName: "type",
  },
  {
    label: "Investment Status",
    className: "column-investment-status",
    columnName: "status",
    dataMapper: OPTIONS_DATA_MAPPER.INVESTMENT_STATUS_MASTER,
  },
  {
    label: "Ticker",
    className: "column-investment-ticker",
    columnName: "ticker",
  },
  {
    label: "CUSIP",
    className: "column-investment-cusip",
    columnName: "cusip",
  },
  {
    label: "Advisor Rep",
    className: "column-investment-advisor-rep",
    columnName: "advisors",
  },
  {
    label: "Clearing Partner",
    className: "column-investment-custodian",
    columnName: "custodian",
  },
];

const ManageInvestmentsPlanContainer = (props) => {
  const { state, dispatch } = useContext(createPlanStore);
  const { history } = props;
  const { planId, flow } = useRouterParams();
  const formName = managePlanFormNames.PLAN_INVESTMENT_FILTER;
  const fields = formFields[formName];
  const [isloading, setIsloading] = useState(false);
  const [investmentData, setInvestmentData] = useState([]);
  useDeepEffect(() => {
    dispatch(clearLocalCacheByModel("listInvestment"));
    setIsloading(true);
    listInvestment(parseInt(planId))
      .then((res) => {
        setIsloading(false);
        setInvestmentData(res);
      })
      .catch((ex) => {
        setIsloading(false);
      });
  }, []);

  const { response: advisorData, loading: loadAdvisor } = useRequest({
    method: listAdvisor,
    payload: parseInt(planId),
    defaultResponse: [],
  });
  const { response: custodian, loading: loadCustodian } = useRequest({
    method: listCustodian,
    payload: parseInt(planId),
    defaultResponse: [],
  });
  const apiData = get(state, "api.data", {});
  const investmentsData = get(investmentData, "investmentPlanMappings", []);
  const advisorsData = get(advisorData, "advisors", []);
  const custodianData = get(custodian, "custodians", []);
  const [filteredValues, setFilteredValues] = useState([]);

  const { response: investmentTypes, loading } = useRequest({
    method: getInvestmentType,
    defaultResponse: [],
  });

  useDeepEffect(() => {
    setFilteredValues(investmentsData);
  }, [investmentsData]);

  const onFormSubmit = (values) => {
    const {
      search,
      investmentType,
      investmentStatus,
      custodian,
      advisors,
    } = values;

    setFilteredValues(
      investmentsData.filter((item) => {
        return (
          (isNull(investmentType) || item.type === investmentType) &&
          (isNull(investmentStatus) || item.status === investmentStatus) &&
          (isEmpty(advisors) ||
            (!isEmpty(item.advisors) &&
              item.advisors.some((_) => advisors.includes(_)))) &&
          (isNull(custodian) ||
            (!isEmpty(item.planInvestment) &&
              item.planInvestment.custodianId === custodian)) &&
          (search === "" ||
            toLower(item.name).indexOf(toLower(search)) !== -1 ||
            toLower(item.ticker).indexOf(toLower(search)) !== -1 ||
            toLower(item.cusip).indexOf(toLower(search)) !== -1)
        );
      })
    );
  };

  const onAddClick = () => {
    history.push(
      getPathWithParam({
        path: MANAGE_PLAN_ROUTES.MANAGE_INVESTMENTS_MASTER,
        pathParam: [flow, planId],
      })
    );
  };

  const buttons = [
    {
      label: "Add Investment",
      type: "button",
      flow: [FLOW_TYPES.ADD, FLOW_TYPES.EDIT],
      onClick: onAddClick,
    },
  ];

  const getNullTableItem = (data) => {
    return !["", null, undefined].includes(data) ? data : <span>-</span>;
  };

  return (
    <LoaderWrapper isLoading={isloading}>
      <ManagePlanLayout
        layoutHeader={"Manage Plan Investments"}
        buttons={buttons}
        pageFlow={FLOW_TYPES.ADD}
      >
        <div className="plan-investment">
          {isEmpty(investmentsData) && (
            <AddPlans
              menu="plan"
              subMenu="investment"
              content="No Investments"
              buttonLabel="ADD INVESTMENT"
              onPrimaryClick={onAddClick}
            />
          )}

          {!isEmpty(investmentsData) && (
            <div>
              <PlanFilterComponent
                fields={fields}
                filteredValues={filteredValues}
                advisorsData={advisorsData}
                custodianData={custodianData}
                investmentTypes={investmentTypes}
                onFormSubmit={onFormSubmit}
              />
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    {columns.map((item, index) => {
                      return (
                        <Table.Th key={index} className={item.className}>
                          {item.label}
                        </Table.Th>
                      );
                    })}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody isLoading={false}>
                  {!isEmpty(filteredValues) ? (
                    filteredValues.map((investment, index) => {
                      return (
                        <Table.Tr key={index}>
                          {columns.map((item, cellIndex) => {
                            const getContent = () => {
                              if (investment[item.columnName] === null)
                                return null;
                              if (!isEmpty(item.link)) {
                                return investment[item.columnName].length >=
                                  15 ? (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip>
                                        {investment[item.columnName]}
                                      </Tooltip>
                                    }
                                  >
                                    <Link
                                      to={getPathWithParam({
                                        path: item.link,
                                        pathParam: [
                                          flow,
                                          planId,
                                          investment.id,
                                        ],
                                      })}
                                    >
                                      {investment[item.columnName].slice(
                                        0,
                                        15
                                      ) + "..."}
                                    </Link>
                                  </OverlayTrigger>
                                ) : (
                                  <Link
                                    to={getPathWithParam({
                                      path: item.link,
                                      pathParam: [flow, planId, investment.id],
                                    })}
                                  >
                                    <AddToolTip
                                      name={investment[item.columnName]}
                                    />
                                  </Link>
                                );
                              }

                              if (
                                (item.columnName === "advisors" ||
                                  item.columnName === "cusip" ||
                                  item.columnName === "ticker") &&
                                investment.type === 1
                              ) {
                                return <AddToolTip name={"Multiple"} />;
                              }

                              if (item.columnName === "type") {
                                return (
                                  <AddToolTip
                                    name={get(
                                      investmentTypes.filter(
                                        (_) =>
                                          _.id === investment[item.columnName]
                                      ),
                                      "0.name"
                                    )}
                                  />
                                );
                              }

                              if (item.columnName === "custodian") {
                                if (investment.type !== 1)
                                  if (investment.planInvestment == null)
                                    return "-";
                                  else
                                    return (
                                      <AddToolTip
                                        name={get(
                                          custodianData.filter(
                                            (_) =>
                                              _.custodianPlanMapId ===
                                              investment.planInvestment
                                                ?.custodianId
                                          ),
                                          "0.custodianCompanyName"
                                        )}
                                      />
                                    );
                                else return <AddToolTip name={"Multiple"} />;
                              }
                              if (item.columnName === "advisors") {
                                if (investment.type !== 1)
                                  return investment.advisors.length > 1 ? (
                                    <AddToolTip name={"Multiple"} />
                                  ) : investment.advisors.length === 1 ? (
                                    <AddToolTip
                                      name={get(
                                        advisorsData.filter(
                                          (_) =>
                                            _.advisorPlanMapId ===
                                            investment.advisors[0]
                                        ),
                                        "0.advisorCompanyName"
                                      )}
                                    />
                                  ) : (
                                    "-"
                                  );
                                else return <AddToolTip name={"Multiple"} />;
                              }
                              if (item.dataMapper) {
                                return (
                                  <AddToolTip
                                    name={
                                      item.dataMapper[
                                        investment[item.columnName]
                                      ]
                                    }
                                  />
                                );
                              }

                              return (
                                <AddToolTip
                                  name={investment[item.columnName]}
                                />
                              );
                            };

                            {
                              return (
                                <Table.Td
                                  key={cellIndex}
                                  className={item.className}
                                >
                                  {getNullTableItem(getContent())}
                                </Table.Td>
                              );
                            }
                          })}
                        </Table.Tr>
                      );
                    })
                  ) : (
                    <div class="d-flex align-items-center justify-content-center">
                      No Records Found
                    </div>
                  )}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </div>
      </ManagePlanLayout>
    </LoaderWrapper>
  );
};

export default ManageInvestmentsPlanContainer;
